import * as React from "react";
import { useCallback, useState } from "react";
import { MeasurementUnits } from "fe-common/graphql";
import styled from "@mui/material/styles/styled";
import { IconButton, MenuItem } from "@mui/material";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { DeleteIcon } from "../common/ActionIcons";
import { QualifierValue } from "./refineByRelation";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";

const DurationContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  width: "100%",
});

const EditDurationContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
});

const DurationDeleteButton = styled(IconButton)(() => ({
  marginLeft: "5px",
}));

const DurationQualifiers = styled("div")(() => ({}));

export interface DurationSliderInterface {
  onChange: (time: number, units: MeasurementUnits) => void;
  onClear: (time: number, units: MeasurementUnits) => void;
  timeUnit: MeasurementUnits;
  time: number;
  disabled?: boolean;
}

const timeUnits = [
  MeasurementUnits.Hours,
  MeasurementUnits.Days,
  MeasurementUnits.Weeks,
  MeasurementUnits.Months,
  MeasurementUnits.Years,
];

const TimeInput = styled(TextField)({
  marginRight: "10px",
  width: "100px",
  borderColor: RosebudColorMap.Accent,
});

const TimeUnitInput = styled(Select)({
  height: "40px",
  borderColor: RosebudColorMap.Accent,
});

interface DurationOption {
  value: number;
  unit: MeasurementUnits;
}

const DurationOptions: DurationOption[] = [
  { value: 1, unit: MeasurementUnits.Days },
  { value: 3, unit: MeasurementUnits.Days },
  { value: 1, unit: MeasurementUnits.Weeks },
  { value: 1, unit: MeasurementUnits.Months },
  { value: 6, unit: MeasurementUnits.Months },
  { value: 1, unit: MeasurementUnits.Years },
];

export const DurationInput = ({
  onChange,
  onClear,
  timeUnit,
  time,
  disabled,
}: DurationSliderInterface) => {
  const shouldShowQualifiers: boolean =
    !time ||
    DurationOptions.some(
      (option) => time === option.value && timeUnit === option.unit,
    );

  const [editDuration, setEditDuration] = useState(!shouldShowQualifiers);
  const handleDurationChange = useCallback(
    (val) => {
      if (val !== time) {
        if (val) {
          onChange(parseInt(val) || null, timeUnit);
        } else {
          onClear(time, timeUnit);
        }
      }
    },
    [timeUnit, onChange, time, onClear],
  );

  const setEditDurationMode = (mode: boolean) => {
    setEditDuration(mode);
  };

  const selectDurationQualifier = (option: DurationOption) => {
    if (option.value === time && option.unit === timeUnit) {
      onClear(time, timeUnit);
    } else {
      onChange(option.value, option.unit);
    }
  };

  const onCloseEditMode = () => {
    onClear(time, timeUnit);
    setEditDurationMode(false);
  };

  const getTimeUnitLabel = (option: DurationOption) => {
    return (
      option.value +
      " " +
      (option.value <= 1
        ? option.unit.substr(0, option.unit.length - 1)
        : option.unit
      ).toLowerCase()
    );
  };

  return (
    <DurationContainer>
      {!editDuration && (
        <DurationQualifiers>
          {DurationOptions.map((option, index) => (
            <QualifierValue
              disabled={disabled}
              key={`duration-opt-${index}`}
              variant={"outlined"}
              clicked={
                time === option.value && timeUnit === option.unit
                  ? "true"
                  : "false"
              }
              size={"small"}
              onClick={() => {
                selectDurationQualifier(option);
              }}
            >
              {getTimeUnitLabel(option)}
            </QualifierValue>
          ))}
          <QualifierValue
            clicked={"false"}
            disabled={disabled}
            variant={"outlined"}
            size={"small"}
            onClick={() => {
              setEditDurationMode(true);
            }}
          >
            {"Other.."}
          </QualifierValue>
        </DurationQualifiers>
      )}

      {editDuration && (
        <EditDurationContainer>
          <TimeInput
            size={"small"}
            disabled={disabled}
            variant={"outlined"}
            InputProps={{ inputProps: { min: 0 } }}
            type={"number"}
            value={!time && time !== 0 ? "" : time}
            onChange={(e) => handleDurationChange(e.target.value)}
          />
          <TimeUnitInput
            variant={"outlined"}
            value={timeUnit}
            disabled={disabled}
            onChange={(e) => onChange(time, e.target.value as MeasurementUnits)}
          >
            {timeUnits.map((tu, index) => (
              <MenuItem key={`time-menu-item-${index}`} value={tu}>
                {tu}
              </MenuItem>
            ))}
          </TimeUnitInput>
          <DurationDeleteButton
            disabled={disabled}
            size={"small"}
            onClick={onCloseEditMode}
          >
            <DeleteIcon />
          </DurationDeleteButton>
        </EditDurationContainer>
      )}
    </DurationContainer>
  );
};
